import confetti from 'canvas-confetti'
import { useEffect, useState } from 'react'
import { cn } from '#app/utils/misc.js'

function CountDownContent({
	date,
	className,
	isLoading = false,
}: {
	date: Date
	className?: string
	isLoading?: boolean
}) {
	const [timeLeft, setTimeLeft] = useState(() => ({
		days: 0,
		hours: 0,
		minutes: 0,
		seconds: 0,
	}))
	const [confettiShown, setConfettiShown] = useState(false)

	useEffect(() => {
		setTimeLeft(calculateTimeLeft(date))

		if (isLoading) return

		const interval = setInterval(() => {
			const newTimeLeft = calculateTimeLeft(date)
			setTimeLeft(newTimeLeft)

			if (Object.values(newTimeLeft).every((value) => value <= 0)) {
				clearInterval(interval)

				// Only show confetti once when countdown reaches zero
				if (!confettiShown) {
					setConfettiShown(true)
					fireConfetti()
				}
			}
		}, 1000)
		return () => clearInterval(interval)
	}, [date, isLoading, confettiShown])

	const displayTimeLeft = isLoading
		? { days: 0, hours: 0, minutes: 0, seconds: 0 }
		: timeLeft

	return (
		<div
			className={cn(
				'grid auto-cols-max grid-flow-col gap-5 text-center',
				className,
				{
					'opacity-60': isLoading,
				},
			)}
		>
			<CountdownUnit value={displayTimeLeft.days} label="Dagar" />
			<CountdownUnit value={displayTimeLeft.hours} label="Timar" />
			<CountdownUnit value={displayTimeLeft.minutes} label="Minutt" />
			<CountdownUnit value={displayTimeLeft.seconds} label="Sekund" />
		</div>
	)
}

function fireConfetti() {
	const duration = 3000
	const animationEnd = Date.now() + duration
	const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 }

	function randomInRange(min: number, max: number) {
		return Math.random() * (max - min) + min
	}

	const interval = setInterval(() => {
		const timeLeft = animationEnd - Date.now()

		if (timeLeft <= 0) {
			return clearInterval(interval)
		}

		const particleCount = 50 * (timeLeft / duration)

		// since particles fall down, start a bit higher than random
		void confetti({
			...defaults,
			particleCount,
			origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
		})
		void confetti({
			...defaults,
			particleCount,
			origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
		})
	}, 250)
}

export function CountDown({
	date,
	className,
}: {
	date: Date
	className?: string
}) {
	return <CountDownContent date={date} className={className} />
}

function CountdownUnit({ value, label }: { value: number; label: string }) {
	return (
		<div className="flex flex-col">
			<span className="countdown font-mono text-5xl">
				<span style={{ '--value': value } as React.CSSProperties}></span>
			</span>
			<span className="text-xs">{label}</span>
		</div>
	)
}

function calculateTimeLeft(date: Date) {
	const difference = Math.max(0, date.getTime() - new Date().getTime())
	return {
		days: Math.floor(difference / (1000 * 60 * 60 * 24)),
		hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
		minutes: Math.floor((difference / 1000 / 60) % 60),
		seconds: Math.floor((difference / 1000) % 60),
	}
}
